import { getPermissionMatrix, savePermissionMatrix } from '@/api/permission';
import configs from '@/configs';
import { buildPermissionTemplate, normalizePermissionMatrix } from '@/configs/permissionFeatures';

const state = {
  matrix: buildPermissionTemplate(configs.ROLES),
  loading: false,
  loaded: false
};

const getters = {
  hasFeatureAccess: (state) => (featureKey, roleValue, minimum = 1) => {
    if (!featureKey) {
      return true;
    }
    const featureMatrix = state.matrix[featureKey];
    if (!featureMatrix) {
      return false;
    }
    const value = featureMatrix[roleValue];
    return Number(value || 0) >= minimum;
  }
};

let inFlightPromise = null;

const mutations = {
  setMatrix(currentState, payload) {
    currentState.matrix = payload;
    currentState.loaded = true;
  },
  setLoading(currentState, status) {
    currentState.loading = status;
  },
  rehydrate(currentState) {
    currentState.matrix = normalizePermissionMatrix(currentState.matrix, configs.ROLES);
    currentState.loading = false;
    currentState.loaded = false;
  },
  reset(currentState) {
    currentState.matrix = buildPermissionTemplate(configs.ROLES);
    currentState.loading = false;
    currentState.loaded = false;
    inFlightPromise = null;
  }
};

const actions = {
  async loadMatrix({ state, commit, rootState }, { force = false } = {}) {
    if (state.loaded && !force && !inFlightPromise) {
      return state.matrix;
    }

    if (inFlightPromise && !force) {
      return inFlightPromise;
    }

    const token = rootState && rootState.authentication ? rootState.authentication.token : null;
    commit('setLoading', true);
    const currentPromise = (async () => {
      try {
        const response = await getPermissionMatrix(token);
        const payload = response && (response.body || response.data || response);
        const normalized = normalizePermissionMatrix(payload ? payload.data : null, configs.ROLES);
        commit('setMatrix', normalized);
        return normalized;
      } catch (error) {
        commit('rehydrate');
        throw error;
      } finally {
        if (inFlightPromise === currentPromise) {
          commit('setLoading', false);
          inFlightPromise = null;
        }
      }
    })();

    inFlightPromise = currentPromise;
    return currentPromise;
  },
  async saveMatrix({ commit, rootState }, matrix) {
    const token = rootState && rootState.authentication ? rootState.authentication.token : null;
    commit('setLoading', true);
    try {
      await savePermissionMatrix(matrix, token);
      const normalized = normalizePermissionMatrix(matrix, configs.ROLES);
      commit('setMatrix', normalized);
      return normalized;
    } finally {
      commit('setLoading', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
